import ReactECharts from 'echarts-for-react';
import { chatTheme } from '../../theme/chat';

const ct = chatTheme.chart;

/**
 * Recursively walk an object and convert string values that look like
 * JS function expressions into real functions so ECharts can execute them.
 */
function reviveFunctions(obj) {
  if (obj === null || obj === undefined) return obj;
  if (typeof obj === 'string') {
    const s = obj.trim();
    if (/^(\(.*?\)|[a-zA-Z_]\w*)\s*=>/.test(s) || s.startsWith('function')) {
      try {
        // eslint-disable-next-line no-new-func
        const fn = new Function('return (' + s + ')')();
        if (typeof fn === 'function') return fn;
      } catch { /* not a valid function, keep as string */ }
    }
    return obj;
  }
  if (Array.isArray(obj)) return obj.map(reviveFunctions);
  if (typeof obj === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(obj)) {
      out[k] = reviveFunctions(v);
    }
    return out;
  }
  return obj;
}

/**
 * Sanitize formatter fields that the LLM set as strings but failed to
 * produce valid JS for.
 */
function sanitizeFormatters(obj) {
  if (obj === null || obj === undefined || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(sanitizeFormatters);

  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if ((k === 'formatter' || k === 'valueFormatter') && typeof v === 'string' && v.includes('=>')) {
      continue;
    }
    out[k] = sanitizeFormatters(v);
  }
  return out;
}

export function ChartRenderer({ option }) {
  if (!option) return null;

  // Merge light theme defaults from centralized theme
  const mergedOption = {
    backgroundColor: 'transparent',
    color: ct.colors,
    textStyle: { color: ct.textSecondary },
    legend: { bottom: 0, left: 'center', textStyle: { color: ct.textMuted, fontSize: 11 }, ...(option.legend || {}) },
    grid: { containLabel: true, left: '3%', right: '4%', top: 40, bottom: 60, ...(option.grid || {}) },
    ...option,
  };

  const revived = reviveFunctions(mergedOption);
  const sanitized = sanitizeFormatters(revived);

  return (
    <div className={`border ${ct.containerBorder} rounded-lg overflow-hidden mt-2 ${ct.containerBg} -mx-2`}>
      <ReactECharts
        option={sanitized}
        style={{ height: '360px', width: '100%' }}
        opts={{ renderer: 'canvas' }}
      />
    </div>
  );
}
